import React, { memo, useMemo, useState } from 'react';
import { Box, Button, Typography, Tooltip } from '@mui/material';
import { ReactComponent as AppleIcon } from '../../assets/images/apple-icon.svg';
import SpotifyIcon from '../../assets/images/spotify-icon.png';
import SpotifyIconBlack from '../../assets/images/spotify-icon-black.png';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import { useStyles } from './styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSelectedGenre,
  resetPodcastsByGenre,
  loadPodcastCharts,
  setShowBrowseGrid,
  setBrowseMode,
  resetBrowseMode,
  setCurrentPodcast,
} from '../../redux/slices/podcastsSlice';
import { updatePodcastUrl } from '../../utils/updatePodcastUrl';
import { calculateRankChange } from '../../utils/rankUtils';
import cx from 'classnames';

export const PodcastCard = memo(({ podcast, hidden, classes: propClasses }) => {
  const { isLight } = useSelector((state) => state.theme);
  const baseClasses = useStyles({ theme: isLight ? 'light' : 'dark' });
  const classes = propClasses || baseClasses;
  const dispatch = useDispatch();
  const { showBrowseGrid, activePlatform, browseModeCountry, allCountries } = useSelector(
    (state) => state.podcasts
  );
  const [imageError, setImageError] = useState(false);

  const countryCode = useMemo(
    () => allCountries.data.find((country) => country.label === browseModeCountry)?.code,
    [browseModeCountry, allCountries]
  );

  const handleImageError = () => {
    setImageError(true);
  };

  const renderRankChange = (podcast) => {
    const rankChange = calculateRankChange(podcast);

    if (rankChange.direction === 'none') {
      return <span>—</span>;
    }

    return rankChange.direction === 'up' ? (
      <span className={classes.historyRankUp}>
        <NorthIcon />
        {rankChange.change}
      </span>
    ) : (
      <span className={classes.historyRankDown}>
        <SouthIcon />
        {rankChange.change}
      </span>
    );
  };

  const handleLoadCharts = (podcast) => {
    dispatch(setSelectedGenre(null));
    dispatch(resetPodcastsByGenre());
    dispatch(loadPodcastCharts(podcast)).then((response) => {
      updatePodcastUrl(podcast.podcastId);
      dispatch(setCurrentPodcast(response.payload));
    });

    dispatch(setShowBrowseGrid(false));
    dispatch(setBrowseMode(false));
    dispatch(resetBrowseMode());
  };

  const genreColor =
    activePlatform === 'apple'
      ? 'var(--podcast-genre-apple-color)'
      : 'var(--podcast-genre-spotify-color)';

  return (
    <div
      className={cx(classes.podcastCard, {
        browseCard: showBrowseGrid,
        [classes.lightThemeBorder]: isLight,
        [classes.lightThemeBoxShadow]: isLight,
      })}
      style={{ '--podcast-genre-color': genreColor }}
      hidden={hidden}
    >
      <Box className={classes.podcastImageWrapper}>
        {!imageError ? (
          <img
            src={podcast.images?.[2]?.label || podcast.images?.[0]?.label}
            alt={podcast.name}
            className={cx(classes.podcastImage, {
              browseImage: showBrowseGrid,
            })}
            loading="lazy"
            decoding="async"
            onError={handleImageError}
          />
        ) : (
          <Box className={cx(classes.podcastImage, classes.fallbackContainer)}>
            <Typography variant="body2" className={classes.fallbackText}>
              {podcast.name}
            </Typography>
          </Box>
        )}
        <Box className={classes.imageOverlay}>
          <Button className={classes.overlayButton} onClick={() => handleLoadCharts(podcast)}>
            Load These Charts
          </Button>
          {activePlatform === 'apple' ? (
            <Button
              className={classes.overlayButton}
              href={`https://podcasts.apple.com/${countryCode}/podcast/id${podcast.podcastId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Apple Podcasts
            </Button>
          ) : (
            <Button
              className={classes.overlayButton}
              href={`https://open.spotify.com/show/${podcast.podcastId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Spotify
            </Button>
          )}
        </Box>
      </Box>
      {podcast.name.length > 31 ? (
        <Tooltip title={podcast.name} placement="top" arrow enterDelay={250} leaveDelay={0}>
          <Typography className={classes.podcastTitle}>{podcast.name}</Typography>
        </Tooltip>
      ) : (
        <Typography className={classes.podcastTitle}>{podcast.name}</Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          overflow: 'hidden',
          width: '100%',
          justifyContent: 'center',
        }}
      >
        <Tooltip title={podcast.artist} placement="top" arrow enterDelay={250} leaveDelay={0}>
          <Typography className={cx(classes.podcastArtist, 'scrolling-text')}>
            {podcast.artist}
          </Typography>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Box className={classes.podcastIcon}>
          {activePlatform === 'apple' ? (
            <AppleIcon />
          ) : isLight ? (
            <img src={SpotifyIconBlack} alt="Spotify" className={classes.podcastIcon} />
          ) : (
            <img src={SpotifyIcon} alt="Spotify" className={classes.podcastIcon} />
          )}
        </Box>
        <Typography className={classes.podcastGenre}>{podcast.genre}</Typography>
      </Box>
      <Typography className={classes.podcastCountry}>{podcast.country}</Typography>
      <Box className={classes.rankContainer}>
        <Typography className={classes.podcastRank}>{podcast.rank}</Typography>
        <Typography className={cx(classes.historyRank, { [classes.lightThemeBorder]: isLight })}>
          {renderRankChange(podcast)}
        </Typography>
      </Box>
    </div>
  );
});
